import { studyQueryKeys } from '@/config/queryKeys';
import { createStudyMutationFn, handleStudyListCacheUpdate } from '@/config/studies';
import { StudyMetadataTable } from '@/pages/Studies/components';
import { Study } from '@/types';
import { EditInPlace } from '@components/index';
import { Badge, BadgeProps, Loading } from '@nautilusbio/nb-design-system';
import { Stack, StackProps } from '@nautilusbio/nb-design-system/Stack';
import clsx from 'clsx';
import { TFunction } from 'i18next';
import React, { type FC } from 'react';

import studyStyles from '../../Studies/styles.module.css';
import styles from '../styles.module.css';

type StudyInfoSectionProps = {
  orgId: string;
  studyId: string;
  data: Study;
  canEdit: boolean;
  isLoading: boolean;
  t: TFunction<'studies', undefined>;
  emptyTxt: string;
  stackSharedProps: Partial<StackProps>;
};

const sharedBadgeProps: Partial<BadgeProps> = {
  size: 'md',
  variant: 'info',
};

export const StudyInfoSection: FC<StudyInfoSectionProps> = ({
  orgId,
  studyId,
  data,
  canEdit,
  isLoading,
  t,
  emptyTxt,
  stackSharedProps,
}) => {
  const cacheKey = studyQueryKeys.detail(orgId, studyId);

  return (
    <Stack fullWidth orientation="horizontal">
      <Stack alignItems="start" className={studyStyles.studyContainer} gap={4} fullWidth orientation="vertical">
        <EditInPlace
          aria-label={t('study.edit_name_aria')}
          as="h1"
          cacheKey={cacheKey}
          canEdit={canEdit}
          fieldName="name"
          mutationFn={createStudyMutationFn(orgId, studyId)}
          onCacheUpdate={handleStudyListCacheUpdate(orgId, studyId)}
          type="input"
          value={data?.name || ''}
        />
        <EditInPlace
          aria-label={t('study.edit_description_aria')}
          as="span"
          cacheKey={cacheKey}
          canEdit={canEdit}
          fieldName="description"
          mutationFn={createStudyMutationFn(orgId, studyId)}
          onCacheUpdate={handleStudyListCacheUpdate(orgId, studyId)}
          type="textarea"
          value={data?.description || ''}
        />
      </Stack>

      <Stack
        alignItems="start"
        className={clsx(studyStyles.studyContainer, styles.sideInfo)}
        gap={4}
        orientation="vertical"
      >
        {(data?.num_samples > 0 || data?.num_instrument_runs > 0) && (
          <Stack fullWidth justifyContent="space-between" orientation="horizontal">
            {data?.num_samples !== undefined && (
              <Stack {...stackSharedProps} fullWidth gap={4}>
                {t('study.samples_label')}{' '}
                <Badge {...sharedBadgeProps}>
                  {isLoading ? <Loading className={studyStyles.isRefetching} type="inline" /> : data.num_samples}
                </Badge>
              </Stack>
            )}

            {data?.num_instrument_runs !== undefined && (
              <Stack {...stackSharedProps} fullWidth gap={4} placeContent="end">
                {t('study.instrument_runs_label')}{' '}
                <Badge {...sharedBadgeProps}>
                  {isLoading ? (
                    <Loading className={studyStyles.isRefetching} type="inline" />
                  ) : (
                    data.num_instrument_runs
                  )}
                </Badge>
              </Stack>
            )}
          </Stack>
        )}

        <StudyMetadataTable study={data} emptyText={emptyTxt} />
      </Stack>
    </Stack>
  );
};
